import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, SettingsIcon, TerminalIcon, ServerIcon, HashIcon, CpuIcon, ActivityIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { udpService, ReceivedUdpMessage } from '../services/udpService';
import { useTranslation } from 'react-i18next';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CustomDropdown({ 
  value, 
  onChange, 
  options, 
  label 
}: { 
  value: string; 
  onChange: (val: string) => void; 
  options: { value: string; label: string }[]; 
  label: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative space-y-1">
      <label className="text-[0.6rem] font-roboto text-slate-500 uppercase">{label}</label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-black/60 border border-white/10 rounded-lg px-2 py-1 text-[0.7rem] text-white font-roboto flex items-center justify-between hover:border-cyan-500/50 transition-all"
        title={label}
      >
        <span className="truncate">{selectedOption?.label || value}</span>
        <ChevronDownIcon className={`w-3 h-3 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-[10000]" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute z-[10001] left-0 right-0 mt-1 bg-[#1e293b] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto custom-scrollbar"
            >
              {options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-1.5 text-left text-[0.7rem] font-roboto flex items-center justify-between transition-colors ${value === opt.value ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-300 hover:bg-white/5'}`}
                >
                  <span>{opt.label}</span>
                  {value === opt.value && <CheckIcon className="w-3 h-3" />}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { t } = useTranslation();
  const [address, setAddress] = useState('iot.seqsys.net');
  const [port, setPort] = useState('30739');
  const [zone, setZone] = useState('11');
  const [type, setType] = useState('P');
  const [id, setId] = useState('1');
  const [telemetry, setTelemetry] = useState('0');
  const [value, setValue] = useState('0'); // Default value 0
  const [hexPreview, setHexPreview] = useState('');
  const [receivedMessages, setReceivedMessages] = useState<ReceivedUdpMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [udpEnabled, setUdpEnabled] = useState(udpService.isEnabled);

  useEffect(() => {
    // Generate hex preview
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    
    // Parse numeric values from string inputs
    const z = parseInt(zone) || 0;
    const i = parseInt(id) || 0;
    const t = parseInt(telemetry) || 0;
    const v = parseFloat(value) || 0;

    view.setUint8(0, z);
    view.setUint8(1, type.charCodeAt(0));
    view.setUint8(2, i);
    view.setUint8(3, t);
    view.setFloat32(4, v, true); // Little Endian

    const hex = Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join(' ')
      .toUpperCase();
    setHexPreview(hex);
  }, [zone, type, id, telemetry, value]);

  useEffect(() => {
    if (isOpen) {
      const unsubscribe = udpService.subscribe((msg) => {
        setReceivedMessages(prev => [msg, ...prev].slice(0, 50));
      });
      return unsubscribe;
    }
  }, [isOpen]);

  const handleSend = async () => {
    setIsSending(true);
    try {
      await udpService.send({
        address,
        port: parseInt(port) || 0,
        zone: parseInt(zone) || 0,
        type,
        id: parseInt(id) || 0,
        telemetry: parseInt(telemetry) || 0,
        value: parseFloat(value) || 0
      });
    } catch (err) {
      alert(`Send failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex justify-center items-start overflow-y-auto p-4 md:p-8 bg-black/60 backdrop-blur-md custom-scrollbar"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-lg bg-[#0f172a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-none relative my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-white/5">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400">
                  <SettingsIcon className="w-4 h-4" />
                </div>
                <h2 className="text-sm font-bold text-white uppercase tracking-wider font-roboto">Telemetry Debugger</h2>
              </div>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-all"
                title={t('debugger.close')}
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Target Config */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label htmlFor="udp-address" className="text-xs font-roboto text-slate-500 uppercase flex items-center gap-2">
                    <ServerIcon className="w-3 h-3" /> Server Address
                  </label>
                  <input 
                    type="text" 
                    id="udp-address"
                    value={address} 
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-cyan-100 focus:outline-none focus:border-cyan-500/50 transition-all font-roboto"
                    placeholder="e.g. 192.168.0.1"
                    title="Server Address"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="udp-port" className="text-xs font-roboto text-slate-500 uppercase flex items-center gap-2">
                    <HashIcon className="w-3 h-3" /> Port
                  </label>
                  <input 
                    type="text" 
                    id="udp-port"
                    value={port}
                    onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-cyan-100 focus:outline-none focus:border-cyan-500/50 transition-all font-roboto"
                    title="Port Number"
                  />
                </div>
              </div>

              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 space-y-3">
                <div className="flex items-center gap-2 mb-1 text-[0.6rem] font-bold text-slate-400 uppercase tracking-widest border-b border-white/5 pb-1">
                  <CpuIcon className="w-3 h-3" /> 8-Byte Binary Packet Builder
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label htmlFor="sensor-zone" className="text-[0.6rem] font-roboto text-slate-500 uppercase">Zone [B0]</label>
                    <input 
                      type="text" id="sensor-zone"
                      value={zone} 
                      onChange={(e) => setZone(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full bg-black/60 border border-white/10 rounded-lg px-2 py-1 text-xs text-white font-roboto"
                      title="Zone Number"
                    />
                  </div>
                  <CustomDropdown 
                    label="Device Type [B1]"
                    value={type}
                    onChange={setType}
                    options={[
                      { value: 'P', label: 'P (Plug)' },
                      { value: 'L', label: 'L (Light)' },
                      { value: 'S', label: 'S (Switch)' },
                      { value: 'C', label: 'C (Curtain)' },
                    ]}
                  />
                  <div className="space-y-1">
                    <label htmlFor="sensor-id" className="text-[0.6rem] font-roboto text-slate-500 uppercase">Device ID [B2]</label>
                    <input 
                      type="text" id="sensor-id"
                      value={id}
                      onChange={(e) => setId(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full bg-black/60 border border-white/10 rounded-lg px-2 py-1 text-xs text-white font-roboto"
                      title="Sensor ID"
                    />
                  </div>
                  <CustomDropdown 
                    label="Telemetry [B3]"
                    value={telemetry}
                    onChange={setTelemetry}
                    options={[
                      { value: "0", label: "0: energy" },
                      { value: "1", label: "1: power" },
                      { value: "2", label: t('debugger.tele_temp') },
                      { value: "3", label: t('debugger.tele_humidity') },
                      { value: "4", label: t('debugger.tele_pressure') },
                      { value: "5", label: t('debugger.tele_tvocs') },
                      { value: "6", label: t('debugger.tele_co2') },
                      { value: "7", label: t('debugger.tele_dust') },
                      { value: "8", label: t('debugger.tele_voice') },
                      { value: "9", label: t('debugger.tele_light') },
                      { value: "10", label: t('debugger.tele_presence') },
                      { value: "11", label: t('debugger.tele_switch') },
                      { value: "12", label: t('debugger.tele_leakage') },
                      { value: "13", label: t('debugger.tele_outlet_onoff') },
                      { value: "14", label: t('debugger.tele_switch_onoff') },
                      { value: "15", label: t('debugger.tele_outlet_mode') },
                      { value: "16", label: t('debugger.tele_switch_mode') },
                      { value: "20", label: t('debugger.tele_ai_voice') },
                      { value: "30", label: t('debugger.tele_curtain_ctrl') },
                      { value: "31", label: t('debugger.tele_curtain_hb') },
                      { value: "32", label: t('debugger.tele_curtain_req') },
                      { value: "33", label: t('debugger.tele_curtain_status') },
                    ]}
                  />
                  <div className="space-y-1 col-span-2">
                    <label htmlFor="sensor-value" className="text-[0.6rem] font-roboto text-slate-500 uppercase">Value [B4-7] (Float32)</label>
                    <input 
                      type="text" id="sensor-value"
                      value={value}
                      onChange={(e) => setValue(e.target.value.replace(/[^0-9.-]/g, ''))}
                      className="w-full bg-black/60 border border-white/10 rounded-lg px-2 py-1 text-xs text-white font-roboto"
                      title="Sensor Value"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-[0.65rem] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 pb-1">
                    <TerminalIcon className="w-3 h-3" /> 8-Byte Packet Structure (Little Endian)
                  </div>
                  <div className="grid grid-cols-8 gap-1">
                    {[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
                      const hex = hexPreview.split(' ')[i] || '00';
                      let label = '';
                      if (i === 0) label = 'ZONE';
                      if (i === 1) label = 'TYPE';
                      if (i === 2) label = 'ID';
                      if (i === 3) label = 'TELE';
                      if (i >= 4) label = 'VAL';
                      
                      return (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <div className={`w-full aspect-square flex items-center justify-center rounded border font-roboto text-[0.65rem] ${i < 3 ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200' : i === 3 ? 'bg-amber-500/30 border-amber-500/50 text-amber-200' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'}`}>
                            {hex}
                          </div>
                          <span className="text-[0.45rem] font-roboto text-slate-600 uppercase">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex flex-col gap-1">
                    <div className="text-[0.65rem] text-slate-600 font-roboto">
                      Target: {address}:{port}
                    </div>
                  </div>
                  <button 
                    onClick={handleSend}
                    disabled={isSending}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg bg-cyan-500 text-white text-sm font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all active:scale-95 ${isSending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-400'}`}
                  >
                    <SendIcon className="w-3.5 h-3.5" /> {isSending ? 'Sending...' : 'Send Packet'}
                  </button>
                </div>
              </div>

              {/* Logs */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-roboto text-slate-500 uppercase">
                  <span className="flex items-center gap-2"><TerminalIcon className="w-3 h-3" /> Incoming Traffic (Port 3002)</span>
                  <button onClick={() => setReceivedMessages([])} className="hover:text-white transition-colors">Clear</button>
                </div>
                <div className="bg-black/80 rounded-xl border border-white/10 h-48 overflow-y-auto p-4 custom-scrollbar font-roboto text-[0.7rem] space-y-1">
                  {receivedMessages.length === 0 ? (
                    <div className="text-slate-700 italic flex items-center justify-center h-full">Waiting for incoming UDP packets...</div>
                  ) : (
                    receivedMessages.map((msg, idx) => (
                      <div key={idx} className="flex gap-4 p-2 border-b border-white/5 hover:bg-white/5 rounded transition-colors group">
                        <span className="text-cyan-600">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                        <span className="text-emerald-500">[{msg.from}:{msg.port}]</span>
                        <span className="text-slate-400">ZONE:{msg.zone} TYPE:{msg.type} ID:{msg.id} TELE:{msg.telemetry} VAL:{msg.value.toFixed(2)}</span>
                        <span className="ml-auto text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">HEX:{msg.raw}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-white/5 bg-white/5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    const newState = !udpEnabled;
                    setUdpEnabled(newState);
                    udpService.toggle(newState);
                  }}
                  className={`px-3 py-1 rounded-full text-[0.65rem] font-bold uppercase transition-colors ${
                    udpEnabled 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 hover:bg-emerald-500/30' 
                      : 'bg-slate-500/20 text-slate-400 border border-slate-500/50 hover:bg-slate-500/30'
                  }`}
                >
                  {udpEnabled ? 'UDP ON' : 'UDP OFF'}
                </button>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${udpEnabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'}`} />
                  <span className="text-[0.6rem] font-roboto text-slate-500 uppercase">
                    Proxy Connection: {udpEnabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
              <ActivityIcon className="w-3.5 h-3.5 text-slate-600" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
